import React, { createContext, useContext, useState, useCallback } from 'react';
import { FeedbackToast, ToastType } from '../components/ui/feedback-toast';
import { useRealm } from './RealmContext';

interface FeedbackContextType {
  showFeedback: (props: {
    type: ToastType;
    title: string;
    description?: string;
    duration?: number;
    actionLabel?: string;
    onAction?: () => void;
  }) => void;
  hideFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackProps, setFeedbackProps] = useState({
    type: 'info' as ToastType,
    title: '',
    description: '',
    duration: 5000,
    actionLabel: '',
    onAction: undefined as (() => void) | undefined
  });
  
  const { currentRealm } = useRealm();
  
  const showFeedback = useCallback((props: {
    type: ToastType;
    title: string;
    description?: string;
    duration?: number;
    actionLabel?: string;
    onAction?: () => void;
  }) => {
    setFeedbackProps({
      type: props.type,
      title: props.title,
      description: props.description || '',
      duration: props.duration || 5000,
      actionLabel: props.actionLabel || '',
      onAction: props.onAction
    });
    setIsOpen(true);
  }, []);
  
  const hideFeedback = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  const handleAction = useCallback(() => {
    if (feedbackProps.onAction) {
      feedbackProps.onAction();
    }
    hideFeedback();
  }, [feedbackProps.onAction, hideFeedback]);
  
  const value = {
    showFeedback,
    hideFeedback
  };
  
  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackToast
        isOpen={isOpen}
        onClose={hideFeedback}
        type={feedbackProps.type}
        title={feedbackProps.title}
        description={feedbackProps.description}
        duration={feedbackProps.duration}
        realm={currentRealm === 'workspace' ? 'sanctuary' : currentRealm}
        actionLabel={feedbackProps.actionLabel}
        onAction={handleAction}
        position="bottom"
        showProgress={true}
      />
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}
